import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Master admin: Auction Calendar bulk import.
 *
 * The owner is entering hundreds of counties and sales from research notes and
 * county websites, which live in spreadsheets. This is the paste path for that.
 *
 * TWO STEPS, ALWAYS. Preview reads the paste and says what it would do to every
 * row. Nothing is written until Import is pressed. The commit does not send the
 * previewed plan back to the server, it sends the same text again and the
 * server re-plans it, so what gets written is computed against the database as
 * it is at that moment rather than as it was when Preview ran.
 *
 * A blank cell means leave that field alone. It never clears a value. Clearing
 * is done in the inline editor on the Counties and sales tab, where it is one
 * deliberate act on one row.
 *
 * THE TARGET IS READ FROM THE HEADER. Choosing what kind of import this is
 * before choosing the file was backwards, because the file already says. The
 * detector lives on the server beside the column declarations it derives from,
 * so this component never carries a second copy of the column lists. A click on
 * a target button still wins, until the text changes again.
 */

const TARGETS = [
  {
    value: 'counties',
    label: 'Counties',
    blurb: 'Updates the office, website, phone, email, seat and notes on counties that already exist. '
      + 'Every US county is already loaded, so this never creates a row.',
    columns: 'county_name or fips_code, state_code, county_seat, sale_runner, sale_website_url, contact_phone, contact_email, notes'
  },
  {
    value: 'sales',
    label: 'Sales',
    blurb: 'Matches an existing sale by county and sale kind, updates it if found, creates it if not. '
      + 'A new sale needs a source_url.',
    columns: 'state_code, county_name or fips_code, sale_kind, platform, typical_month, next_sale_date, '
      + 'registration_deadline, list_url, status, source_url, notes'
  },
  {
    value: 'states',
    label: 'States',
    blurb: 'Updates the state level summaries. All fifty states are seeded, so this never creates a row.',
    columns: 'state_code, sale_type, redemption_summary, interest_or_penalty_summary, otc_route_summary, '
      + 'primary_statute_citation, notes'
  }
];

const ACTION_BADGE = {
  create: 'badge-success',
  update: 'badge-info',
  error: 'badge-danger'
};

/**
 * Only the header row decides the target, so a prefix is all the detector needs
 * and a large paste never travels just to answer this question.
 */
const DETECT_PREFIX = 8000;

function CalendarBulkImport({
  api, apiBase, token, inputStyle, cellLabelStyle, panelStyle,
  onOpenCounty, onOpenCounties, onOpenState, onFlash, onImported
}) {
  const [target, setTarget] = useState('counties');
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [detected, setDetected] = useState(null);
  const [overridden, setOverridden] = useState(false);
  const fileRef = useRef(null);
  const detectTimer = useRef(null);

  // Read inside the debounced callback, where component state would be stale.
  const overriddenRef = useRef(false);

  const spec = TARGETS.find(t => t.value === target);

  /** Ask the server what this header is for. */
  const detect = useCallback(async (value) => {
    if (value.trim() === '') {
      setDetected(null);
      return;
    }
    try {
      const data = await api('/bulk/detect', {
        method: 'POST',
        body: JSON.stringify({ text: value.slice(0, DETECT_PREFIX) })
      });
      if (!data) return;
      setDetected(data);
      if (data.target && !overriddenRef.current) setTarget(data.target);
    } catch (err) {
      // Detection is a convenience. When it cannot answer, the buttons are
      // left exactly as the owner last set them.
      setDetected({ target: null, reason: `Could not read the header: ${err.message}` });
    }
  }, [api]);

  /**
   * Any edit invalidates a preview: it no longer describes this text.
   *
   * THE RESULT IS LEFT ALONE. It is a record of what an import did, not a
   * description of what is in the box, so editing does not make it wrong. That
   * matters after a partial failure: the lines that failed are kept in front
   * of the owner while they fix them, instead of vanishing on the first
   * keystroke. Pressing Preview or Import again clears it, which is when it
   * really has been superseded.
   */
  const changeText = (value) => {
    setText(value);
    setPlan(null);

    // New text is a new question, so detection gets to answer it again even if
    // the owner had overridden the last one.
    overriddenRef.current = false;
    setOverridden(false);

    if (detectTimer.current) window.clearTimeout(detectTimer.current);
    detectTimer.current = window.setTimeout(() => detect(value), 350);
  };

  const changeTarget = (value) => {
    setTarget(value);
    setPlan(null);
    setResult(null);
    overriddenRef.current = true;
    setOverridden(true);
  };

  useEffect(() => () => {
    if (detectTimer.current) window.clearTimeout(detectTimer.current);
  }, []);

  const readFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      changeText(String(reader.result || ''));
      setFileName(file.name);
    };
    reader.onerror = () => setError(`Could not read ${file.name}.`);
    reader.readAsText(file);
  };

  const clearAll = () => {
    changeText('');
    setFileName('');
    setError('');
    setDetected(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const preview = async () => {
    if (busy) return;
    setError('');
    setResult(null);
    setBusy(true);
    try {
      const data = await api('/bulk/preview', {
        method: 'POST',
        body: JSON.stringify({ target, text })
      });
      if (data) setPlan(data.plan);
    } catch (err) {
      setPlan(null);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Write the rows.
   *
   * A SECOND PRESS CANNOT START A SECOND IMPORT. The button disables on the
   * re-render, and this guard covers the gap before it, the same way the save
   * buttons on the Counties and sales tab do. Two commits of one paste would
   * not corrupt anything, because the server re-plans and the second pass
   * would find nothing left to change, but it would write a second set of
   * history rows saying a field changed when it did not.
   *
   * ON A CLEAN IMPORT THE FORM IS EMPTIED. Nothing was left of the old
   * behaviour to tell the owner it had finished: the paste box still held the
   * whole spreadsheet, the file was still chosen, Preview was still live, and
   * the only new thing was a table at the bottom of a long page.
   *
   * ON ANYTHING LESS THAN CLEAN NOTHING IS CLEARED, because the pasted text is
   * the only copy of the lines that need fixing.
   */
  const commit = async () => {
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      const data = await api('/bulk/commit', {
        method: 'POST',
        body: JSON.stringify({ target, text })
      });
      if (data) {
        // BEFORE the result renders, never after. The States and Counties tabs
        // still hold whatever they loaded before this import, the result table
        // links straight into them, and an edit form filled from a stale row
        // offers the owner blanks to save over what was just imported.
        if (onImported) await onImported();
        setResult(data);
        setPlan(null);
        if (data.totals.failed === 0 && data.totals.skipped === 0) {
          setText('');
          setFileName('');
          setDetected(null);
          if (fileRef.current) fileRef.current.value = '';
        }
        onFlash(`Imported ${data.totals.created} created, ${data.totals.updated} updated`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /** Back to an empty form, from the finished result. */
  const importAnother = () => {
    setResult(null);
    clearAll();
  };

  /** The template download needs the auth header, so it goes through fetch. */
  const downloadTemplate = async (which) => {
    try {
      const response = await fetch(`${apiBase}/master-admin/calendar/bulk/template/${which}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Could not build that template.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `calendar-${which}-template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const willWrite = plan
    ? plan.rows.filter(r => r.action === 'create' || (r.action === 'update' && r.changes.length > 0)).length
    : 0;

  const detectedLabel = detected && detected.target
    ? (TARGETS.find(t => t.value === detected.target) || {}).label
    : '';

  // An import where every row either wrote or had nothing to write. Anything
  // skipped for an error, and anything that failed on the way in, is not
  // finished work: the text stays where it is and the form stays open.
  const clean = Boolean(result) && result.totals.failed === 0 && result.totals.skipped === 0;

  // The states an import touched, each one still needing a human verdict.
  const importedStates = result && result.target === 'states'
    ? Array.from(new Set(result.written.map(row => row.stateCode).filter(Boolean)))
    : [];

  return (
    <div className="card">
      {clean ? (
        <p style={{ color: 'var(--color-text-light)', marginTop: 0 }}>
          That import is finished. Start another one when you are ready.
        </p>
      ) : (
      <>
      <p style={{ color: 'var(--color-text-light)', marginTop: 0 }}>
        Paste cells copied from a spreadsheet, or choose a .csv file. The first row must be a
        header naming the columns. Column order does not matter, and any column this import does
        not recognise is reported and ignored.
      </p>
      <p style={{ color: 'var(--color-text-light)' }}>
        The header decides what kind of import this is, so you can choose the file first. Pick a
        different target below if you want to override what it read.
      </p>
      <p style={{ color: 'var(--color-text-light)' }}>
        A blank cell leaves that field as it is. It never clears a value. To clear something, edit
        the row on the Counties and sales tab.
      </p>

      {error && (
        <div className="card card-error" style={{ marginBottom: '16px' }}>
          <span>{error}</span>
          <button onClick={() => setError('')} className="btn btn-secondary" style={{ marginLeft: '12px' }}>
            Dismiss
          </button>
        </div>
      )}

      {/* ---------------------------------------------------- what to import */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {TARGETS.map(t => (
          <button
            key={t.value}
            className={`btn ${target === t.value ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => changeTarget(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {detected && (
        <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
          {detected.target
            ? `Read as ${detectedLabel}. ${detected.reason}`
            : detected.reason}
          {overridden && detected.target && detected.target !== target
            && ` You chose ${spec.label} instead, so that is what will run.`}
        </p>
      )}

      <div style={{ ...panelStyle, marginTop: 0, marginBottom: '16px' }}>
        <p style={{ margin: 0, color: 'var(--color-text)' }}>{spec.blurb}</p>
        <p style={{ margin: '8px 0 0', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
          Columns: {spec.columns}
        </p>
        <div style={{ marginTop: '10px' }}>
          <button className="btn btn-secondary" onClick={() => downloadTemplate(target)}>
            Download the {spec.label.toLowerCase()} template
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------- input */}
      <div style={{ marginBottom: '12px' }}>
        <label style={cellLabelStyle} htmlFor="bulk-text">
          Paste here, tab separated from a spreadsheet or comma separated from a .csv
        </label>
        <textarea
          id="bulk-text"
          style={{ ...inputStyle, minHeight: '180px', fontFamily: 'monospace', resize: 'vertical' }}
          value={text}
          onChange={e => changeText(e.target.value)}
          placeholder={'state_code\tcounty_name\tcontact_phone\nWI\tBurnett\t715-349-2173'}
        />
      </div>

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '20px' }}>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={readFile}
          style={{ color: 'var(--color-text)' }}
        />
        {fileName && (
          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Loaded {fileName}</span>
        )}
        <button className="btn btn-primary" onClick={preview} disabled={busy || text.trim() === ''}>
          {busy ? 'Working' : 'Preview'}
        </button>
        <button className="btn btn-secondary" onClick={clearAll} disabled={busy}>Clear</button>
      </div>
      </>
      )}

      {/* ----------------------------------------------------------- preview */}
      {plan && (
        <div style={panelStyle}>
          <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>
            Preview, {plan.label.toLowerCase()}, read as {plan.delimiter}
          </h3>

          <p style={{ color: 'var(--color-text)' }}>
            {plan.totals.total} row{plan.totals.total === 1 ? '' : 's'}:{' '}
            <strong>{plan.totals.create} to create</strong>,{' '}
            <strong>{plan.totals.update} matched for update</strong>
            {plan.totals.unchanged > 0 && ` (${plan.totals.unchanged} of them already match, so nothing will be written)`},{' '}
            <strong>{plan.totals.error} with errors</strong>.
          </p>

          {plan.unknownColumns.length > 0 && (
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
              Ignored column{plan.unknownColumns.length === 1 ? '' : 's'}: {plan.unknownColumns.join(', ')}
            </p>
          )}

          {plan.totals.error > 0 && (
            <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
              Rows with errors are skipped. The rest still go through.
            </p>
          )}

          <div style={{ overflowX: 'auto', maxHeight: '460px', overflowY: 'auto', marginTop: '10px' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Action</th>
                  <th>Row</th>
                  <th>What changes</th>
                </tr>
              </thead>
              <tbody>
                {plan.rows.map(row => (
                  <tr key={row.line}>
                    <td style={{ fontFamily: 'monospace' }}>{row.line}</td>
                    <td>
                      <span className={`badge ${ACTION_BADGE[row.action] || 'badge-secondary'}`}>
                        {row.action === 'update' && row.changes.length === 0 ? 'no change' : row.action}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{row.label || '-'}</td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {row.action === 'error' && (
                        <span style={{ color: 'var(--color-danger-text, var(--color-text))' }}>{row.error}</span>
                      )}
                      {row.action === 'create' && (
                        <span style={{ color: 'var(--color-text-light)' }}>
                          New {row.saleKind ? row.saleKind.replace(/_/g, ' ') : 'row'}
                        </span>
                      )}
                      {row.action === 'update' && row.changes.length === 0 && (
                        <span style={{ color: 'var(--color-text-muted)' }}>Already matches, nothing to write</span>
                      )}
                      {row.action === 'update' && row.changes.map(change => (
                        <div key={change.field} style={{ color: 'var(--color-text-light)' }}>
                          <strong style={{ color: 'var(--color-text)' }}>{change.field}</strong>
                          {': '}
                          {change.from === null ? 'empty' : change.from} to {change.to}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '14px' }}>
            <button className="btn btn-primary" onClick={commit} disabled={busy || willWrite === 0}>
              {busy ? 'Importing' : `Import ${willWrite} row${willWrite === 1 ? '' : 's'}`}
            </button>
            <button className="btn btn-secondary" style={{ marginLeft: '8px' }} onClick={() => setPlan(null)}>
              Cancel
            </button>
            {willWrite === 0 && (
              <span style={{ marginLeft: '12px', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                Nothing here would change anything.
              </span>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------ result */}
      {result && (
        <div style={panelStyle}>
          <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>
            {clean ? 'Import finished' : 'Imported, with rows left to fix'}
          </h3>
          <p style={{ color: 'var(--color-text)' }}>
            {result.totals.created} created, {result.totals.updated} updated,{' '}
            {result.totals.skipped} skipped for errors, {result.totals.failed} failed.
          </p>

          {/* What to do next, which is different for each target. A states
              import is the one that leaves a job behind: it never stamps a
              state verified, so every state it touched is still waiting on a
              human verdict and the owner has to be told that in the place
              they just finished working. */}
          {clean && result.target === 'states' && (
            <>
              <p style={{ color: 'var(--color-text)' }}>
                {importedStates.length} state{importedStates.length === 1 ? '' : 's'} updated.
                An import never marks a state verified. Review each one and click Verify on
                the States tab.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {importedStates.map(code => (
                  <button
                    key={code}
                    className="btn btn-secondary"
                    onClick={() => onOpenState && onOpenState(code)}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </>
          )}

          {clean && result.target !== 'states' && (
            <p style={{ color: 'var(--color-text)' }}>
              {result.totals.created} row{result.totals.created === 1 ? '' : 's'} created and{' '}
              {result.totals.updated} updated.{' '}
              <button
                className="btn btn-secondary"
                style={{ marginLeft: '4px' }}
                onClick={() => onOpenCounties && onOpenCounties()}
              >
                Open the Counties and sales tab
              </button>
            </p>
          )}

          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
            The States and Counties tabs have been reloaded, so they show what this import wrote.
          </p>

          {!clean && (
            <p style={{ color: 'var(--color-text)' }}>
              Your pasted text and your file were kept, so you can fix the lines below and
              import again. The rows that already went through will show as no change the
              second time, so importing the whole thing again is safe.
            </p>
          )}

          {result.failures.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              {result.failures.map(failure => (
                <div key={failure.line} style={{ color: 'var(--color-text-light)', fontSize: '0.85rem' }}>
                  Line {failure.line}, {failure.label}: {failure.error}
                </div>
              ))}
            </div>
          )}

          {result.totals.skipped > 0 && (
            <p style={{ color: 'var(--color-text-light)', fontSize: '0.85rem' }}>
              {result.totals.skipped} row{result.totals.skipped === 1 ? ' was' : 's were'} skipped
              before writing, for the errors the preview listed. Press Preview again to see
              them line by line.
            </p>
          )}

          <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Action</th>
                  <th>Row</th>
                  <th>Fields logged</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {result.written.length === 0 && (
                  <tr><td colSpan={5} style={{ color: 'var(--color-text-light)' }}>Nothing needed writing.</td></tr>
                )}
                {result.written.map(row => (
                  <tr key={`${row.line}-${row.action}`}>
                    <td style={{ fontFamily: 'monospace' }}>{row.line}</td>
                    <td>
                      <span className={`badge ${ACTION_BADGE[row.action] || 'badge-secondary'}`}>{row.action}</span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{row.label}</td>
                    <td>{row.changed}</td>
                    <td>
                      {row.countyName && row.stateCode && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => onOpenCounty(row.stateCode, row.countyName)}
                        >
                          Open county
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {clean && (
            <div style={{ marginTop: '14px' }}>
              <button className="btn btn-primary" onClick={importAnother}>Import another file</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CalendarBulkImport;
